/**
 * SOS Button — fixed circular emergency button.
 * Press → switch to Emergencias channel, broadcast sos_alert with GPS,
 * push a red message into the chat log.
 */
import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import useStore from '../store/useStore';
import { getSocket } from '../hooks/useSocket';

function getCurrentPosition(timeout = 5000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve(null),
      { timeout, maximumAge: 10000, enableHighAccuracy: true }
    );
  });
}

export default function SOSButton() {
  const user = useStore(s => s.user);
  const channels = useStore(s => s.channels);
  const assignedChannels = useStore(s => s.assignedChannels);
  const setActiveChannel = useStore(s => s.setActiveChannel);
  const addMessage = useStore(s => s.addMessage);
  const addToast = useStore(s => s.addToast);
  const [armed, setArmed] = useState(false);
  const [sending, setSending] = useState(false);

  const findEmergencyChannel = () => {
    const all = [...(channels || []), ...(assignedChannels || [])];
    return all.find(c => /emerg/i.test(c.name || '')) || null;
  };

  const trigger = async () => {
    if (sending) return;
    setSending(true);
    try {
      const coords = await getCurrentPosition();
      const socket = getSocket();
      const ch = findEmergencyChannel();

      if (ch) {
        setActiveChannel(ch);
        if (socket?.connected) socket.emit('channel:setActive', { channelId: ch.id });
      }

      if (socket?.connected) {
        socket.emit('sos_alert', {
          channelId: ch?.id || null,
          lat: coords?.lat ?? null,
          lng: coords?.lng ?? null,
          accuracy: coords?.accuracy ?? null,
          message: `🚨 SOS — ${user?.username || 'Usuario'}`,
        });
      }

      const now = new Date();
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      const coordTxt = coords ? `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}` : 'sin GPS';

      if (ch) {
        addMessage(ch.id, {
          id: 'sos_' + Date.now() + Math.random(),
          type: 'sos',
          userId: user?.id,
          username: user?.username,
          avatarColor: user?.avatarColor,
          content: `🚨 SOS — ${user?.username} · ${coordTxt} · ${hh}:${mm}`,
          coords,
          timestamp: now.toISOString(),
        });
      }

      addToast('🚨 SOS enviado a operaciones', 'error');
    } finally {
      setSending(false);
      setArmed(false);
    }
  };

  if (armed) {
    return (
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2 animate-fade-in">
        <div
          className="px-4 py-2 rounded-md text-xs font-bold text-white"
          style={{ background: '#FF2D55', boxShadow: '0 8px 24px rgba(255,45,85,.45)' }}
        >
          ¿Confirmar SOS?
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setArmed(false)}
            className="px-3 py-2 rounded-md text-xs font-semibold bg-white border border-n-200 text-n-700 shadow-apple-sm"
          >
            Cancelar
          </button>
          <button
            onClick={trigger}
            disabled={sending}
            className="px-4 py-2 rounded-md text-xs font-bold text-white"
            style={{
              background: '#FF2D55',
              boxShadow: '0 8px 24px rgba(255,45,85,.45)',
              animation: 'pulseGlowRed 1.4s ease-in-out infinite',
            }}
          >
            {sending ? 'Enviando…' : 'CONFIRMAR SOS'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => setArmed(true)}
      title="SOS — emergencia"
      aria-label="SOS"
      className="fixed bottom-4 right-4 z-40 w-14 h-14 rounded-full flex items-center justify-center text-white"
      style={{
        background: 'linear-gradient(135deg, #FF2D55, #FF375F)',
        boxShadow: '0 10px 28px rgba(255,45,85,.55), 0 4px 12px rgba(255,45,85,.30)',
        border: '3px solid #fff',
      }}
    >
      <AlertTriangle size={22} />
      <span className="sr-only">SOS</span>
    </button>
  );
}
